// src/features/AIRecommendations/components/RecommendationsDashboard.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  TrendingUp,
  WaterDrop,
  LocalFireDepartment,
  Build,
  Schedule,
  PriorityHigh,
  CheckCircle,
  Info
} from '@mui/icons-material';
import { useI18n } from '../../../i18n/i18n';

const PriorityIndicator = ({ priority, t }) => {
  const config = {
    critical: { color: 'error', icon: <PriorityHigh /> },
    high: { color: 'error', icon: <PriorityHigh /> },
    medium: { color: 'warning', icon: <Info /> },
    low: { color: 'info', icon: <Info /> },
    info: { color: 'success', icon: <CheckCircle /> }
  };

  const { color, icon } = config[priority] || config.info;

  return (
    <Chip
      icon={icon}
      label={t(`ai_priority_${priority}`)}
      color={color}
      size="small"
      variant="filled"
    />
  );
};

const ImpactIndicator = ({ impact, t }) => {
  const impactConfig = {
    high: { color: 'error', label: t('impact_high_production') },
    medium: { color: 'warning', label: t('impact_medium_production') },
    low: { color: 'info', label: t('impact_low_production') },
    resource_savings: { color: 'success', label: t('impact_resource_savings') }
  };

  const { color, label } = impactConfig[impact] || impactConfig.medium;

  return (
    <Chip
      label={label}
      color={color}
      variant="outlined"
      size="small"
    />
  );
};

const RecommendationCard = ({ recommendation, t, onApply }) => {
  const getIconByType = (type) => {
    const icons = {
      efficiency: <TrendingUp />,
      resource_optimization: <WaterDrop />,
      shift_optimization: <Schedule />,
      equipment: <Build />,
      process: <LocalFireDepartment />
    };
    return icons[type] || <Info />;
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ color: 'primary.main', mt: 0.5 }}>
            {getIconByType(recommendation.type)}
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {recommendation.title}
              </Typography>
              <PriorityIndicator priority={recommendation.priority} t={t} />
              <ImpactIndicator impact={recommendation.impact} t={t} />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {recommendation.description}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('ai_recommended_actions')}
              </Typography>
              <List dense>
                {recommendation.actions.map((action, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText primary={action} />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t('ai_expected_impact')}: {recommendation.expectedImprovement}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('implementation_time')}: {recommendation.implementationTime}
              </Typography>
            </Stack>
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={() => onApply(recommendation)}
            sx={{ minWidth: 100 }}
          >
            {t('common_apply')}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

const RecommendationsDashboard = ({ recommendations, analysis, isLoading, onApplyRecommendation }) => {
  const { t } = useI18n();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ textAlign: 'center', py: 4 }}>
            {t('common_loading')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="h6">
              {t('ai_all_systems_optimal')}
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {t('ai_no_recommendations')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const criticalRecommendations = recommendations.filter(r => r.priority === 'critical' || r.priority === 'high');
  const mediumRecommendations = recommendations.filter(r => r.priority === 'medium');
  const lowRecommendations = recommendations.filter(r => r.priority === 'low' || r.priority === 'info');

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {t('ai_recommendations_title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {analysis?.summary || t('ai_recommendations_subtitle')}
        </Typography>

        {/* Критичні рекомендації */}
        {criticalRecommendations.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" color="error.main" sx={{ fontWeight: 600, mb: 2 }}>
              {t('ai_priority_high')} ({criticalRecommendations.length})
            </Typography>
            {criticalRecommendations.map(rec => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                t={t}
                onApply={onApplyRecommendation}
              />
            ))}
          </Box>
        )}

        {/* Рекомендації середнього пріоритету */}
        {mediumRecommendations.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600, mb: 2 }}>
              {t('ai_priority_medium')} ({mediumRecommendations.length})
            </Typography>
            {mediumRecommendations.map(rec => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                t={t}
                onApply={onApplyRecommendation}
              />
            ))}
          </Box>
        )}

        {/* Рекомендації низького пріоритету */}
        {lowRecommendations.length > 0 && (
          <Box>
            <Typography variant="h6" color="info.main" sx={{ fontWeight: 600, mb: 2 }}>
              {t('ai_priority_low')} ({lowRecommendations.length})
            </Typography>
            {lowRecommendations.map(rec => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                t={t}
                onApply={onApplyRecommendation}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecommendationsDashboard;


